/**
 * Animation utility functions for GSAP animations
 */

/**
 * Initialize animations for elements with animation classes
 * @param {Object} gsap - GSAP instance
 * @param {Object} ScrollTrigger - ScrollTrigger plugin instance
 */
export function initViewportAnimations(gsap, ScrollTrigger) {
  // Animate the second section (az-padding__a)
  animateSecondSection(gsap, ScrollTrigger);

  // Animate the content columns
  animateContentColumns(gsap, ScrollTrigger);

  // Initialize fade-in-up animations for elements with the utility class
  initFadeInUpAnimations(gsap, ScrollTrigger);

  // Initialize clip-path animations for elements with the utility class
  initClipPathAnimations(gsap, ScrollTrigger);
}

/**
 * Animate the second section
 * @param {Object} gsap - GSAP instance
 * @param {Object} ScrollTrigger - ScrollTrigger plugin instance
 */
function animateSecondSection(gsap, ScrollTrigger) {
  // Get the target section
  const targetSection = document.querySelector(".az-padding__a");
  if (!targetSection) return;

  // Get the elements to animate
  const h1 = targetSection.querySelector(".az-h1");
  const lead = targetSection.querySelector(".az-lead");
  const heroTextParagraphs = targetSection.querySelectorAll(".az-hero__txt p");
  const circleSvg = targetSection.querySelector(".circle-svg");

  // Set initial states
  gsap.set([h1, lead, ...heroTextParagraphs], {
    opacity: 0,
    y: 40,
  });

  // Set initial state for circle
  gsap.set(circleSvg, {
    top: "20px",
    left: "60px",
    scale: 0.8,
  });

  // Create a single ScrollTrigger for the entire section
  ScrollTrigger.create({
    trigger: targetSection,
    start: "top 85%", // Triggers when section is 15% from bottom
    toggleActions: "play none none none", // Play once, don't repeat
    onEnter: () => {
      // Create a timeline for sequential animation
      const tl = gsap.timeline();

      // 1. Circle animation
      tl.to(circleSvg, {
        top: 0,
        duration: 0.4,
        ease: "power2.out",
      })
        .to(
          circleSvg,
          {
            left: 0,
            duration: 0.4,
            ease: "power2.out",
          },
          "-=0.4"
        )
        .to(circleSvg, {
          scale: 1,
          duration: 0.4,
          ease: "power2.out",
          delay: 0.1,
        })
        // 2. H1 animation
        .to(h1, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
        })
        // 3. Lead animation
        .to(lead, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
        })
        // 4. Paragraphs animation with stagger
        .to(heroTextParagraphs, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.08,
          ease: "power2.out",
        });
    },
  });
}

/**
 * Animate the content columns with same style as second section
 * @param {Object} gsap - GSAP instance
 * @param {Object} ScrollTrigger - ScrollTrigger plugin instance
 */
function animateContentColumns(gsap, ScrollTrigger) {
  // Get all content columns
  const contentColumns = document.querySelectorAll(".az-col__content");

  contentColumns.forEach((column) => {
    // Get elements to animate within each column
    const circleSvg = column.querySelector(".circle-svg");
    const h2 = column.querySelector(".az-h2");
    const paragraphs = column.querySelectorAll("p");

    if (!circleSvg) return;

    // Set initial states
    if (h2) {
      gsap.set(h2, {
        opacity: 0,
        y: 40,
      });
    }

    if (paragraphs.length) {
      gsap.set(paragraphs, {
        opacity: 0,
        y: 40,
      });
    }

    // Set initial state for circle
    gsap.set(circleSvg, {
      top: "20px",
      left: "60px",
      scale: 0.8,
    });

    // Create ScrollTrigger for this column
    ScrollTrigger.create({
      trigger: column,
      start: "top 85%",
      toggleActions: "play none none none",
      onEnter: () => {
        // Create a timeline for sequential animation
        const tl = gsap.timeline();

        // 1. Circle animation
        tl.to(circleSvg, {
          top: 0,
          duration: 0.4,
          ease: "power2.out",
        })
          .to(
            circleSvg,
            {
              left: 0,
              duration: 0.4,
              ease: "power2.out",
            },
            "-=0.4"
          )
          .to(circleSvg, {
            scale: 1,
            duration: 0.4,
            ease: "power2.out",
            delay: 0.1,
          });

        // 2. H2 animation
        if (h2) {
          tl.to(h2, {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: "power2.out",
          });
        }

        // 3. Paragraphs animation with stagger
        if (paragraphs.length) {
          tl.to(paragraphs, {
            opacity: 1,
            y: 0,
            duration: 0.4,
            stagger: 0.08,
            ease: "power2.out",
          });
        }
      },
    });
  });
}

/**
 * Initialize fade-in-up animations for elements with the utility class
 * @param {Object} gsap - GSAP instance
 * @param {Object} ScrollTrigger - ScrollTrigger plugin instance
 */
function initFadeInUpAnimations(gsap, ScrollTrigger) {
  // Get all elements with the utility class, including nested elements
  const fadeInUpElements = document.querySelectorAll(".az-fade__up");

  // Set initial states with proper transform handling
  fadeInUpElements.forEach((element) => {
    // Store original display property
    const originalDisplay = window.getComputedStyle(element).display;

    // Set initial state with proper transform
    gsap.set(element, {
      opacity: 0,
      y: 40,
      display: originalDisplay, // Preserve original display property
      force3D: true, // Force 3D transforms for better performance
    });
  });

  // Create ScrollTrigger for each element
  fadeInUpElements.forEach((element) => {
    ScrollTrigger.create({
      trigger: element,
      start: "top 85%",
      toggleActions: "play none none none", // Play once, don't repeat
      onEnter: () => {
        // Create a timeline for better control
        const tl = gsap.timeline({
          defaults: { ease: "power2.out" },
        });

        // Animate with proper transform handling
        tl.to(element, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          force3D: true,
        });
      },
    });
  });
}

/**
 * Initialize clip-path animations for elements with the utility class
 * @param {Object} gsap - GSAP instance
 * @param {Object} ScrollTrigger - ScrollTrigger plugin instance
 */
function initClipPathAnimations(gsap, ScrollTrigger) {
  // Get all elements with the utility class
  const clipPathElements = document.querySelectorAll(".az-clip__circle");

  // Set initial states
  clipPathElements.forEach((element) => {
    // Set initial left-to-right clip-path (start with nothing visible)
    gsap.set(element, {
      clipPath: "inset(0 100% 0 0)", // Clip everything from right side
    });
  });

  // Create ScrollTrigger for each element
  clipPathElements.forEach((element) => {
    ScrollTrigger.create({
      trigger: element,
      start: "top 85%", // Triggers when element is 15% from bottom
      toggleActions: "play none none none", // Play once, don't repeat
      onEnter: () => {
        gsap.to(element, {
          clipPath: "inset(0 0% 0 0)", // Reveal everything from left to right
          duration: 1.2, // Same duration as before
          ease: "power2.out",
          delay: 0.5, // Same delay as before
        });
      },
    });
  });
}
